import React from "react";
import { Breadcrumbs, Box, Link, Typography } from "@mui/material";
import { Link as RouterLink, matchPath, useLocation } from "react-router-dom";

type BreadcrumbItem = {
  label: string;
  to?: string;
};

const toolLabels: Record<string, string> = {
  "tax-calculator": "Tax Calculator",
  "mortgage-calculator": "Mortgage Calculator",
  "mind-map": "Mind Map",
  "expenses-comparison": "Expenses Comparison",
  "debt-payoff-forecasting": "Debt Payoff Forecasting",
  "shopping-groceries": "Shopping & Groceries",
  "housing-forecast": "Housing Forecast",
  charts: "Charts",
  rules: "Rules",
  api: "API",
  "wife-value-calculator": "Spouse Value Calculator",
  "spouse-value-calculator": "Spouse Value Calculator",
};

const routeLabels: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/expenses": "Expenses",
  "/tools/expenses-comparison": "Expenses Comparison",
  "/income": "Income",
  "/debts": "Debts",
  "/goals": "Goals",
  "/accounts": "Accounts",
  "/transactions": "Transactions",
  "/groups": "Groups",
  "/tools": "Tools",
  "/settings": "Settings",
  "/docs": "Documentation",
  "/login": "Login",
  "/register": "Register",
  "/terms": "Terms of Service",
  "/privacy": "Privacy Policy",
};

const publicRoutes = new Set([
  "/login",
  "/register",
  "/docs",
  "/terms",
  "/privacy",
]);

const AppBreadcrumbs: React.FC = () => {
  const location = useLocation();
  const path = location.pathname;

  const buildCrumbs = (): BreadcrumbItem[] => {
    const toolMatch = matchPath("/tools/:toolId", path);
    if (toolMatch?.params?.toolId) {
      const toolId = toolMatch.params.toolId;
      return [
        { label: "Dashboard", to: "/dashboard" },
        { label: "Tools", to: "/tools" },
        { label: toolLabels[toolId] || "Tool" },
      ];
    }

    const groupNewMatch = matchPath("/groups/new", path);
    if (groupNewMatch) {
      return [
        { label: "Dashboard", to: "/dashboard" },
        { label: "Groups", to: "/groups" },
        { label: "New Group" },
      ];
    }

    const groupMembersMatch = matchPath("/groups/:id/members", path);
    if (groupMembersMatch) {
      return [
        { label: "Dashboard", to: "/dashboard" },
        { label: "Groups", to: "/groups" },
        { label: "Members" },
      ];
    }

    const groupSharedMatch = matchPath("/groups/:id/shared", path);
    if (groupSharedMatch) {
      return [
        { label: "Dashboard", to: "/dashboard" },
        { label: "Groups", to: "/groups" },
        { label: "Shared" },
      ];
    }

    const groupDetailMatch = matchPath("/groups/:id", path);
    if (groupDetailMatch) {
      return [
        { label: "Dashboard", to: "/dashboard" },
        { label: "Groups", to: "/groups" },
        { label: "Group" },
      ];
    }

    if (publicRoutes.has(path)) {
      return [{ label: routeLabels[path] || "Page" }];
    }

    if (path === "/dashboard") {
      return [{ label: routeLabels[path] || "Dashboard" }];
    }

    return [
      { label: "Dashboard", to: "/dashboard" },
      { label: routeLabels[path] || "Page" },
    ];
  };

  const crumbs = buildCrumbs();

  if (crumbs.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        pt: 2,
        pb: 1,
        px: { xs: 1, sm: 2 },
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Breadcrumbs sx={{ minWidth: 0 }}>
        {crumbs.map((crumb, index) =>
          crumb.to ? (
            <Link
              key={`${crumb.label}-${index}`}
              component={RouterLink}
              to={crumb.to}
              underline="hover"
              color="inherit"
            >
              {crumb.label}
            </Link>
          ) : (
            <Typography key={`${crumb.label}-${index}`} color="text.primary">
              {crumb.label}
            </Typography>
          )
        )}
      </Breadcrumbs>
    </Box>
  );
};

export default AppBreadcrumbs;
